import React, { useEffect, useState, useMemo, useRef } from "react";
import "./CommunityList.css";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupIcon from "@mui/icons-material/Group";
import ClearIcon from "@mui/icons-material/Clear";
import SchoolIcon from "@mui/icons-material/School";
import ForumIcon from "@mui/icons-material/Forum";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SparklesIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Snackbar, Alert } from "@mui/material";
import { getAllCommunities, joinCommunity } from "../../../api/community";

export default function CommunityList() {
  const navigate = useNavigate();
  const tabsTrackRef = useRef(null);
  const userDetails = useSelector((state) => state.userDetails?.userDetails);
  const userCity = userDetails?.addresses?.[0]?.city || userDetails?.city || "";
  const userPoliticalAffiliation = userDetails?.politicalAffiliation || "";
  const userProfessions = Array.isArray(userDetails?.professions) ? userDetails.professions : [];

  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("all"); // 'all' | 'my' | 'cities' | 'political' | 'professions' | 'gated'
  const [search, setSearch] = useState("");
  const [joiningId, setJoiningId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (tabsTrackRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsTrackRef.current;
      setCanScrollLeft(scrollLeft > 2);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
    }
  };

  useEffect(() => {
    checkScroll();
    const timeout = setTimeout(checkScroll, 150);
    window.addEventListener("resize", checkScroll);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", checkScroll);
    };
  }, [communities, selectedTab]);

  const showToast = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCommunities = async () => {
    try {
      setLoading(true);
      const res = await getAllCommunities(null, search);
      if (res?.success) {
        setCommunities(res.data || []);
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to load communities.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommunities();
  }, []);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    fetchCommunities();
  };

  const handleClearSearch = () => {
    setSearch("");
    setTimeout(() => {
      getAllCommunities(null, "").then((res) => {
        if (res?.success) setCommunities(res.data || []);
      });
    }, 0);
  };

  const handleJoin = async (e, comm) => {
    e.stopPropagation();
    try {
      setJoiningId(comm.communityId);
      const res = await joinCommunity(comm.communityId);
      if (res?.success) {
        showToast(`🎉 Welcome to ${comm.name}!`, "success");
        setCommunities((prev) =>
          prev.map((c) =>
            c.communityId === comm.communityId
              ? { ...c, isMember: true, memberCount: (c.memberCount || 0) + 1 }
              : c
          )
        );
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to join community.", "error");
    } finally {
      setJoiningId(null);
    }
  };

  // Tab Filter Counts
  const tabCounts = useMemo(() => {
    return {
      all: communities.length,
      my: communities.filter((c) => c.isMember).length,
      cities: communities.filter((c) => c.communityType === "City").length,
      political: communities.filter((c) => c.communityType === "Political").length,
      professions: communities.filter((c) => c.communityType === "Profession").length,
      gated: communities.filter(
        (c) =>
          c.communityType !== "City" &&
          c.communityType !== "Political" &&
          c.communityType !== "Profession" &&
          c.isGated
      ).length,
    };
  }, [communities]);

  const filteredCommunities = useMemo(() => {
    return communities.filter((c) => {
      const matchSearch =
        !search?.trim() ||
        c.name?.toLowerCase().includes(search.trim().toLowerCase());

      if (!matchSearch) return false;

      if (selectedTab === "my") return c.isMember;
      if (selectedTab === "cities") return c.communityType === "City";
      if (selectedTab === "political") return c.communityType === "Political";
      if (selectedTab === "professions") return c.communityType === "Profession";
      if (selectedTab === "gated")
        return (
          c.communityType !== "City" &&
          c.communityType !== "Political" &&
          c.communityType !== "Profession" &&
          c.isGated
        );
      return true;
    });
  }, [communities, selectedTab, search]);

  // Unjoined spotlight cards (User is eligible but hasn't joined yet)
  const unjoinedSpotlights = useMemo(() => {
    const list = [];

    if (userCity) {
      const cityComm = communities.find(
        (c) =>
          c.communityType === "City" &&
          c.city?.trim().toLowerCase() === userCity.trim().toLowerCase() &&
          !c.isMember
      );
      if (cityComm) {
        list.push({
          ...cityComm,
          spotlightType: "City",
          badgeText: "Local City Space",
          icon: <LocationCityIcon sx={{ fontSize: 15 }} />,
          accentColor: "#38BDF8",
          requirementText: `Verified Resident of ${cityComm.city}`,
        });
      }
    }

    if (userPoliticalAffiliation) {
      const polComm = communities.find(
        (c) =>
          c.communityType === "Political" &&
          c.politicalAffiliation?.trim().toLowerCase() === userPoliticalAffiliation.trim().toLowerCase() &&
          !c.isMember
      );
      if (polComm) {
        list.push({
          ...polComm,
          spotlightType: "Political",
          badgeText: "Political Circle",
          icon: <HowToVoteRoundedIcon sx={{ fontSize: 15 }} />,
          accentColor: "#818CF8",
          requirementText: `Supporter of ${polComm.politicalAffiliation}`,
        });
      }
    }

    if (userProfessions.length > 0) {
      const profComms = communities.filter(
        (c) =>
          c.communityType === "Profession" &&
          c.profession &&
          userProfessions.some((p) => p.trim().toLowerCase() === c.profession.trim().toLowerCase()) &&
          !c.isMember
      );
      profComms.forEach((pComm) => {
        list.push({
          ...pComm,
          spotlightType: "Profession",
          badgeText: "Professional Circle",
          icon: <WorkOutlineRoundedIcon sx={{ fontSize: 15 }} />,
          accentColor: "#F59E0B",
          requirementText: `Verified ${pComm.profession}`,
        });
      });
    }

    // Unlocked Certification / Merit communities
    const certComms = communities.filter(
      (c) =>
        (c.communityType === "Certification" || !!c.requiredCertificationId) &&
        c.isUnlocked &&
        !c.isMember
    );
    certComms.forEach((cComm) => {
      list.push({
        ...cComm,
        spotlightType: "Certification",
        badgeText: "Merit Circle",
        icon: <WorkspacePremiumIcon sx={{ fontSize: 15 }} />,
        accentColor: "#10B981",
        requirementText: cComm.requiredCertificationName ? `Certified in ${cComm.requiredCertificationName}` : "Earned Verified Certificate",
      });
    });

    return list;
  }, [communities, userCity, userPoliticalAffiliation, userProfessions]);

  const handleScroll = (direction) => {
    if (tabsTrackRef.current) {
      const scrollAmount = direction === "left" ? -180 : 180;
      tabsTrackRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const FILTER_TABS = [
    { id: "all", label: "All" },
    { id: "my", label: "My Circles" },
    { id: "cities", label: "Cities" },
    { id: "political", label: "Political" },
    { id: "professions", label: "Professions" },
    { id: "gated", label: "Merit Gated" },
  ];

  return (
    <div className="CommunityListContainer">
      {/* Page Header */}
      <div className="community-header-section">
        <div className="community-header-left">
          <div className="community-header-badge">
            <VerifiedIcon sx={{ fontSize: 14 }} />
            <span>Civic & Merit Ecosystem</span>
          </div>
          <h1 className="community-page-title">Communities & Circles</h1>
          <p className="community-page-subtitle">
            Connect, collaborate, and participate in exclusive decision-making circles unlocked by your verified certifications, professional titles, political affiliations, and local residency.
          </p>
        </div>

        <div className="community-header-stats">
          <div className="stat-pill">
            <span className="stat-num">{tabCounts.all}</span>
            <span className="stat-label">Available</span>
          </div>
          <div className="stat-pill stat-pill-joined">
            <span className="stat-num">{tabCounts.my}</span>
            <span className="stat-label">Joined</span>
          </div>
        </div>
      </div>

      {/* Unlocked For You Spotlight Hub */}
      {unjoinedSpotlights.length > 0 && (
        <div className="community-spotlight-hub">
          <div className="spotlight-hub-header">
            <div className="spotlight-hub-title">
              <SparklesIcon sx={{ fontSize: 18, color: "#48D1CC" }} />
              <span>Unlocked For You</span>
              <span className="spotlight-hub-count">{unjoinedSpotlights.length} Pending Action</span>
            </div>
            <span className="spotlight-hub-sub">You qualify for these exclusive spaces based on your profile details</span>
          </div>

          <div className="spotlight-cards-grid">
            {unjoinedSpotlights.map((item) => (
              <div
                key={item.communityId}
                className={`spotlight-card spotlight-${item.spotlightType.toLowerCase()}`}
                onClick={() => navigate(`/communities/${item.slug}`)}
              >
                <div className="spotlight-card-top">
                  <div className="spotlight-tag" style={{ color: item.accentColor, borderColor: `${item.accentColor}35`, backgroundColor: `${item.accentColor}15` }}>
                    {item.icon}
                    <span>{item.badgeText}</span>
                  </div>
                  <span className="spotlight-members">
                    <GroupIcon sx={{ fontSize: 13 }} />
                    {item.memberCount || 0}
                  </span>
                </div>

                <h3 className="spotlight-name">{item.name}</h3>
                <p className="spotlight-description">{item.description || item.requirementText}</p>

                <div className="spotlight-card-bottom">
                  <span className="spotlight-qual-badge">
                    <VerifiedIcon sx={{ fontSize: 13, color: item.accentColor }} />
                    {item.requirementText}
                  </span>
                  <button
                    className="spotlight-join-btn"
                    onClick={(e) => handleJoin(e, item)}
                    disabled={joiningId === item.communityId}
                  >
                    {joiningId === item.communityId ? "Joining..." : "Join Circle"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control Deck: Search + Filter Tabs */}
      <div className="community-control-deck">
        <div className="community-search-bar-row">
          <div className="community-search-wrapper">
            <input
              type="text"
              placeholder="Search by community name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit(e)}
              className="community-search-input"
            />
            {search && (
              <button className="search-clear-btn" onClick={handleClearSearch} type="button" aria-label="Clear search">
                <ClearIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>
          
          <div className="filter-summary-info">
            <span>Showing <strong>{filteredCommunities.length}</strong> of <strong>{communities.length}</strong></span>
          </div>
        </div>

        {/* Clean Segmented Filter Pills with Scroll Indicator & Visible Scrollbar */}
        <div className="community-filter-bar">
          <div className={`filter-tabs-wrapper ${canScrollLeft ? "has-scroll-left" : ""} ${canScrollRight ? "has-scroll-right" : ""}`}>
            {canScrollLeft && (
              <button
                className="tabs-scroll-btn scroll-btn-left"
                onClick={() => handleScroll("left")}
                type="button"
                aria-label="Scroll filters left"
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
              </button>
            )}

            <div
              className="filter-tabs-track"
              ref={tabsTrackRef}
              onScroll={checkScroll}
            >
              {FILTER_TABS.map((tab) => {
                const count = tabCounts[tab.id] ?? 0;
                const isActive = selectedTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`filter-tab-pill ${isActive ? "active" : ""}`}
                    onClick={() => setSelectedTab(tab.id)}
                  >
                    <span className="filter-tab-text">{tab.label}</span>
                    <span className="filter-tab-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {canScrollRight && (
              <button
                className="tabs-scroll-btn scroll-btn-right"
                onClick={() => handleScroll("right")}
                type="button"
                aria-label="Scroll filters right"
              >
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Communities Grid */}
      {loading ? (
        <div className="community-loading-container">
          <div className="community-spinner" />
          <p>Discovering exclusive spaces...</p>
        </div>
      ) : filteredCommunities.length === 0 ? (
        <div className="community-empty-container">
          <div className="empty-icon-wrap">
            <ForumIcon sx={{ fontSize: 36, color: "#48D1CC" }} />
          </div>
          <h3>No Communities Found</h3>
          <p>
            {selectedTab === "my"
              ? "You haven't joined any communities yet. Discover and join your unlocked circles above!"
              : selectedTab === "cities"
              ? "No city space active for your profile yet. Update your city in profile details to discover your local residency circle."
              : selectedTab === "political"
              ? "No political space active for your profile yet. Select your political party in profile details to unlock your party circle."
              : selectedTab === "professions"
              ? "No profession circle active for your profile yet. Add your titles and professions in profile details to unlock your industry circles."
              : "No communities match your current search or filter."}
          </p>
          {selectedTab === "cities" && (
            <button className="btn-empty-action" onClick={() => navigate("/profile-details/edit-address")}>
              <LocationOnIcon sx={{ fontSize: 16 }} /> Update City Address
            </button>
          )}
          {selectedTab === "political" && (
            <button className="btn-empty-action" onClick={() => navigate("/profile-details/edit-political-affiliation")}>
              <HowToVoteRoundedIcon sx={{ fontSize: 16 }} /> Select Political Party
            </button>
          )}
          {selectedTab === "professions" && (
            <button className="btn-empty-action" onClick={() => navigate("/profile-details/edit-professions")}>
              <WorkOutlineRoundedIcon sx={{ fontSize: 16 }} /> Select Professions & Titles
            </button>
          )}
        </div>
      ) : (
        <motion.div layout className="community-grid">
          <AnimatePresence>
            {filteredCommunities.map((comm) => {
              const isCity = comm.communityType === "City";
              const isPolitical = comm.communityType === "Political";
              const isProfession = comm.communityType === "Profession";

              return (
                <motion.div
                  layout
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ duration: 0.2 }}
                  key={comm.communityId}
                  className={`community-card ${comm.isUnlocked ? "is-unlocked" : "is-locked"} ${isCity ? "type-city" : ""} ${isPolitical ? "type-political" : ""} ${isProfession ? "type-profession" : ""}`}
                  onClick={() => navigate(`/communities/${comm.slug}`)}
                >
                  {/* Card Banner */}
                  <div
                    className="community-card-banner"
                    style={{
                      backgroundImage: comm.coverImageUrl ? `url(${comm.coverImageUrl})` : "none",
                    }}
                  >
                    <div className="community-card-banner-overlay" />

                    <div className="card-top-row">
                      {isCity ? (
                        <div className="category-tag city-tag">
                          <LocationOnIcon sx={{ fontSize: 12 }} /> City Space
                        </div>
                      ) : isPolitical ? (
                        <div className="category-tag political-tag">
                          <HowToVoteRoundedIcon sx={{ fontSize: 12 }} /> Political
                        </div>
                      ) : isProfession ? (
                        <div className="category-tag profession-tag">
                          <WorkOutlineRoundedIcon sx={{ fontSize: 12 }} /> Profession
                        </div>
                      ) : (
                        <div className="category-tag merit-tag">
                          {comm.isGated ? "Merit Space" : "Open Forum"}
                        </div>
                      )}

                      {/* Status Pill */}
                      <div className="status-badge-wrap">
                        {comm.isMember ? (
                          <span className="status-pill status-joined">
                            <VerifiedIcon sx={{ fontSize: 12 }} /> Joined
                          </span>
                        ) : comm.isUnlocked ? (
                          <span className="status-pill status-unlocked">
                            <LockOpenIcon sx={{ fontSize: 12 }} /> Unlocked
                          </span>
                        ) : (
                          <span className="status-pill status-locked">
                            <LockIcon sx={{ fontSize: 12 }} /> Gated
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card Content Body */}
                  <div className="community-card-body">
                    <div className="card-header-identity">
                      {comm.avatarUrl ? (
                        <img src={comm.avatarUrl} alt={comm.name} className="community-avatar-img" />
                      ) : (
                        <div className={`community-avatar-placeholder ${isCity ? "city-av" : isPolitical ? "pol-av" : isProfession ? "prof-av" : ""}`}>
                          {isCity ? (
                            <LocationCityIcon sx={{ fontSize: 20 }} />
                          ) : isPolitical ? (
                            <HowToVoteRoundedIcon sx={{ fontSize: 20 }} />
                          ) : isProfession ? (
                            <WorkOutlineRoundedIcon sx={{ fontSize: 20 }} />
                          ) : (
                            comm.name.charAt(0).toUpperCase()
                          )}
                        </div>
                      )}
                      <div className="card-header-titles">
                        <h3 className="card-title-text" title={comm.name}>
                          {comm.name}
                        </h3>
                        <span className="card-member-text">
                          <GroupIcon sx={{ fontSize: 13 }} />
                          {comm.memberCount || 0} members
                        </span>
                      </div>
                    </div>

                    <p className="card-description-text">{comm.description || "Official collaborative community space."}</p>

                    {/* Gating Requirement Capsule */}
                    <div className="card-requirement-box">
                      {isCity ? (
                        <div className="req-row">
                          <LocationOnIcon sx={{ fontSize: 14, color: "#38BDF8" }} />
                          <span>Residents of {comm.city}{comm.state ? `, ${comm.state}` : ""}</span>
                        </div>
                      ) : isPolitical ? (
                        <div className="req-row">
                          <HowToVoteRoundedIcon sx={{ fontSize: 14, color: "#818CF8" }} />
                          <span>Supporters of {comm.politicalAffiliation || comm.name}</span>
                        </div>
                      ) : isProfession ? (
                        <div className="req-row">
                          <WorkOutlineRoundedIcon sx={{ fontSize: 14, color: "#F59E0B" }} />
                          <span>Verified {comm.profession || comm.name}</span>
                        </div>
                      ) : comm.isGated ? (
                        <div className="req-row">
                          <SchoolIcon sx={{ fontSize: 14, color: "#48D1CC" }} />
                          <span>Requires: {comm.requiredCertificationName}</span>
                        </div>
                      ) : (
                        <div className="req-row">
                          <LockOpenIcon sx={{ fontSize: 14, color: "#4ade80" }} />
                          <span>Open Civic Space</span>
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <div className="card-action-wrapper">
                      {comm.isMember ? (
                        <button className="card-btn btn-view-community">
                          <span>Enter Space</span>
                          <ArrowForwardIcon sx={{ fontSize: 15 }} />
                        </button>
                      ) : comm.isUnlocked ? (
                        <button
                          className="card-btn btn-join-unlocked"
                          onClick={(e) => handleJoin(e, comm)}
                          disabled={joiningId === comm.communityId}
                        >
                          {joiningId === comm.communityId ? "Joining..." : "Join Community"}
                        </button>
                      ) : isCity ? (
                        <button
                          className="card-btn btn-gated-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            showToast(`Exclusive to verified residents of ${comm.city}.`, "info");
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: 14 }} /> {comm.city} Residents Only
                        </button>
                      ) : isPolitical ? (
                        <button
                          className="card-btn btn-gated-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/profile-details/edit-political-affiliation");
                          }}
                        >
                          <HowToVoteRoundedIcon sx={{ fontSize: 14 }} /> Select {comm.politicalAffiliation} to Unlock
                        </button>
                      ) : isProfession ? (
                        <button
                          className="card-btn btn-gated-outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/profile-details/edit-professions");
                          }}
                        >
                          <WorkOutlineRoundedIcon sx={{ fontSize: 14 }} /> Select {comm.profession} in Profile
                        </button>
                      ) : (
                        <button
                          className="card-btn btn-gated-cert"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              comm.requiredCertificationId
                                ? `/certifications/${comm.requiredCertificationId}`
                                : "/certifications"
                            );
                          }}
                        >
                          <SchoolIcon sx={{ fontSize: 14 }} /> Certify to Unlock
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
